import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(params.get("search") || "");
  const [filterOpen, setFilterOpen] = useState(false);

  const category = params.get("category") || "";
  const subcategory = params.get("subcategory") || "";
  const availability = params.get("availability") || "";
  const sort = params.get("sort") || "name";

  useEffect(() => {
    setCategoriesLoading(true);
    api.get("/categories")
      .then((r) => setCategories(r.data))
      .finally(() => setCategoriesLoading(false));
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get("/products", {
      params: {
        category: category || undefined,
        subcategory: subcategory || undefined,
        availability: availability || undefined,
        search: params.get("search") || undefined,
        sort,
        limit: 48,
      },
    }).then((r) => {
      setProducts(r.data.items);
      setTotal(r.data.total);
    }).finally(() => setLoading(false));
  }, [params]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };

  const activeCat = categories.find((c) => c.slug === category);

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="catalog-page">
      {/* Breadcrumb / title */}
      <div className="mb-6">
        <div className="text-xs text-muted-foreground">
          <Link to="/" className="hover:text-primary">Home</Link> / <span className="text-foreground">Products</span>
          {activeCat && <> / <span className="text-foreground">{activeCat.name}</span></>}
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold mt-2">
          {activeCat ? activeCat.name : "All products"}
        </h1>
        {activeCat?.description && (
          <p className="text-muted-foreground mt-2 max-w-2xl text-sm">{activeCat.description}</p>
        )}
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        {/* Sidebar */}
        <aside className={`lg:col-span-3 ${filterOpen ? "block" : "hidden lg:block"}`} data-testid="filters-sidebar">
          <div className="bg-white border border-gray-100 rounded-xl p-5 sticky top-32 space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-semibold">Filters</h3>
              <button className="lg:hidden p-1" onClick={() => setFilterOpen(false)}><X className="w-4 h-4" /></button>
            </div>

            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Category</div>
              <div className="space-y-2">
                <button
                  onClick={() => {
                    console.log("Current URL Params:");
                    console.log(Object.fromEntries(params.entries()));
                    const next = new URLSearchParams(params);
                    next.delete("category");
                    next.delete("subcategory");
                    console.log("Next URL Params:");
                    console.log(Object.fromEntries(next.entries()));
                    console.log("Next Query String:");
                    console.log(next.toString());
                    setParams(next);
                  }}
                  className={`block w-full text-left text-sm ${!category ? "text-primary font-semibold" : "text-foreground/70 hover:text-primary"}`}
                  data-testid="filter-cat-all"
                >All categories</button>
                {categoriesLoading ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="h-9 rounded-md bg-muted/50 animate-pulse" />
                  ))
                ) : (
                  categories.map((c) => (
                    <div key={c.slug}>
                      <button
                        onClick={() => {
                          const next = new URLSearchParams(params);
                          next.set("category", c.slug);
                          next.delete("subcategory");
                          setParams(next);
                        }}
                        className={`block w-full text-left text-sm ${category === c.slug ? "text-primary font-semibold" : "text-foreground/70 hover:text-primary"}`}
                        data-testid={`filter-cat-${c.slug}`}
                      >{c.name}</button>
                      {category === c.slug && c.subcategories?.length > 0 && (
                        <div className="mt-1.5 ml-3 pl-3 border-l border-gray-100 space-y-1.5">
                          {c.subcategories.map((s) => (
                            <button
                              key={s.slug}
                              onClick={() => setParam("subcategory", s.slug === subcategory ? "" : s.slug)}
                              className={`block text-xs text-left ${subcategory === s.slug ? "text-primary font-semibold" : "text-muted-foreground hover:text-primary"}`}
                              data-testid={`filter-sub-${s.slug}`}
                            >{s.name}</button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Availability</div>
              <div className="space-y-2">
                {[
                  { v: "", label: "All" },
                  { v: "in_stock", label: "In stock" },
                  { v: "out_of_stock", label: "Out of stock" },
                ].map((a) => (
                  <label key={a.v} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`filter-avail-${a.v || "all"}`}>
                    <Checkbox checked={availability === a.v} onCheckedChange={() => setParam("availability", availability === a.v ? "" : a.v)} />
                    {a.label}
                  </label>
                ))}
              </div>
            </div>
          </div>
        </aside>

        {/* Results */}
        <div className="lg:col-span-9">
          <div className="bg-white border border-gray-100 rounded-xl p-4 flex flex-wrap items-center gap-3 mb-5">
            <form onSubmit={(e) => { e.preventDefault(); setParam("search", search); }} className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search within results…" className="pl-9 h-10" data-testid="catalog-search" />
            </form>
            <Button variant="outline" className="lg:hidden" onClick={() => setFilterOpen(true)} data-testid="open-filters">
              <SlidersHorizontal className="w-4 h-4 mr-1" /> Filters
            </Button>
            <Select value={sort} onValueChange={(v) => setParam("sort", v)}>
              <SelectTrigger className="w-44" data-testid="sort-select"><SelectValue placeholder="Sort" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Name (A–Z)</SelectItem>
                <SelectItem value="newest">Newest first</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground ml-auto" data-testid="result-count">{total} products</span>
          </div>

          {loading ? (
            <div className="space-y-4">
              <div className="rounded-xl border border-gray-100 bg-white px-5 py-4 text-sm text-muted-foreground text-center">
                Loading products...
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="aspect-[4/5] bg-muted/40 animate-pulse rounded-xl" />
                ))}
              </div>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground bg-white border border-gray-100 rounded-xl" data-testid="no-results">
              No products found. Try different filters.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 lg:gap-6">
              {products.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
